/**
 * CSV export of the visible (filtered + sorted) lot list.
 *
 * Exports the decision-relevant fields: identity, bids, estimates, meat,
 * walk-away range, fees, lifecycle, and close time. Respects the active
 * search/filter so users can export exactly what they're looking at.
 *
 * @module portal/modules/csvExport
 */

import type { Lot, PortalSettings, State } from '../types';
import { parseCurrencyValue } from './formatting';
import { calculateMeatOnBone, calculateWalkAwayRange, resolveLotFees } from './lotOperations';
import { computeCostBasis, computeRealizedProfit, getLifecycleStatus } from './lifecycle';

const HEADERS = [
  'Title',
  'Source',
  'Auction',
  'Lot #',
  'URL',
  'Location',
  'Current Bid',
  'Next Bid',
  'Low Estimate',
  'High Estimate',
  'Meat Low',
  'Meat High',
  'Stay Under Bid',
  'Never Exceed Bid',
  'My Max Bid',
  'Buyer Premium %',
  'Tax %',
  'Seller Costs %',
  'Fixed Cost',
  'Status',
  'Favorite',
  'My Bid',
  'Closes At',
] as const;

function csvEscape(value: unknown): string {
  if (value == null) return '';
  const text = String(value);
  if (/[",\r\n]/.test(text)) {
    return '"' + text.replace(/"/g, '""') + '"';
  }
  return text;
}

function money(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '';
}

function lotCloseIso(lot: Lot, getEffectiveCloseTime: (lot: Lot) => number | null): string {
  const ts = getEffectiveCloseTime(lot);
  if (ts == null || !Number.isFinite(ts)) return '';
  try {
    return new Date(ts).toISOString();
  } catch {
    return '';
  }
}

export function buildLotsCsv(
  lots: Lot[],
  settings: PortalSettings | null | undefined,
  getEffectiveCloseTime: (lot: Lot) => number | null
): string {
  const rows: string[] = [HEADERS.join(',')];

  for (const lot of lots) {
    if (!lot) continue;
    const meat = calculateMeatOnBone(lot, settings);
    const range = calculateWalkAwayRange(lot, settings);
    const fees = resolveLotFees(lot, settings);
    const currentBid = parseCurrencyValue(
      lot.currentBid != null ? lot.currentBid : lot.currentBidRaw
    );
    const nextBid = parseCurrencyValue(
      lot.nextRequiredBid != null ? lot.nextRequiredBid : lot.nextRequiredBidRaw
    );
    const low = parseCurrencyValue(
      lot.lowSellValue != null ? lot.lowSellValue : lot.lowSellValueRaw
    );
    const high = parseCurrencyValue(
      lot.highSellValue != null ? lot.highSellValue : lot.highSellValueRaw
    );

    rows.push(
      [
        csvEscape(lot.title),
        csvEscape(lot.source),
        csvEscape(lot.auctionTitle),
        csvEscape(lot.lotNumber),
        csvEscape(lot.url),
        csvEscape(lot.location),
        money(currentBid),
        money(nextBid),
        money(low),
        money(high),
        money(meat?.meatLow ?? null),
        money(meat?.meatHigh ?? null),
        money(range?.stayUnderBid ?? null),
        money(range?.neverExceedBid ?? null),
        money(typeof lot.userMaxBid === 'number' ? lot.userMaxBid : null),
        csvEscape(fees.buyerPremiumPct),
        csvEscape(fees.taxRatePct),
        csvEscape(fees.sellerCostsPct),
        money(fees.fixedLotCost),
        csvEscape(lot.lifecycleStatus || 'watching'),
        lot.favorite ? 'yes' : '',
        money(typeof lot.bidAmount === 'number' ? lot.bidAmount : null),
        csvEscape(lotCloseIso(lot, getEffectiveCloseTime)),
      ].join(',')
    );
  }

  return rows.join('\r\n');
}

/** Trigger a browser download of the given CSV text. */
export function downloadCsv(csv: string, filenamePrefix = 'lotlingo-lots'): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filenamePrefix}-${stamp}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Export the currently visible lots (search + filters applied) as CSV. */
export function exportVisibleLots(
  state: State,
  visibleLots: Lot[],
  getEffectiveCloseTime: (lot: Lot) => number | null
): number {
  const csv = buildLotsCsv(visibleLots, state.settings, getEffectiveCloseTime);
  downloadCsv(csv);
  return visibleLots.length;
}

const INVENTORY_HEADERS = [
  'Title',
  'Source',
  'Auction',
  'URL',
  'Status',
  'Hammer Price',
  'Fees Paid',
  'Cost Basis',
  'Low Estimate',
  'High Estimate',
  'Sale Price',
  'Sale Channel',
  'Sold At',
  'Realized Profit',
  'Won At',
] as const;

/**
 * Inventory & outcomes CSV: every won/listed/sold lot with cost basis and
 * realized economics — the bookkeeping view of the reseller pipeline.
 */
export function buildInventoryCsv(
  lots: Lot[],
  settings: PortalSettings | null | undefined
): string {
  const rows: string[] = [INVENTORY_HEADERS.join(',')];

  for (const lot of lots) {
    if (!lot || lot.deleted) continue;
    const status = getLifecycleStatus(lot);
    if (status !== 'won' && status !== 'listed' && status !== 'sold') continue;

    const low = parseCurrencyValue(
      lot.lowSellValue != null ? lot.lowSellValue : lot.lowSellValueRaw
    );
    const high = parseCurrencyValue(
      lot.highSellValue != null ? lot.highSellValue : lot.highSellValueRaw
    );

    rows.push(
      [
        csvEscape(lot.title),
        csvEscape(lot.source),
        csvEscape(lot.auctionTitle),
        csvEscape(lot.url),
        csvEscape(status),
        money(lot.outcome?.hammerPrice ?? null),
        money(lot.outcome?.feesPaid ?? null),
        money(computeCostBasis(lot, settings)),
        money(low),
        money(high),
        money(lot.outcome?.salePrice ?? null),
        csvEscape(lot.outcome?.saleChannel),
        csvEscape(lot.outcome?.soldAt ? lot.outcome.soldAt.slice(0, 10) : ''),
        money(computeRealizedProfit(lot, settings)),
        csvEscape(lot.outcome?.decidedAt ? lot.outcome.decidedAt.slice(0, 10) : ''),
      ].join(',')
    );
  }

  return rows.join('\r\n');
}

/** Download the inventory CSV; returns the number of exported rows. */
export function exportInventoryCsv(state: State): number {
  const csv = buildInventoryCsv(state.lots, state.settings);
  downloadCsv(csv, 'lotlingo-inventory');
  return csv.split('\r\n').length - 1;
}
